import dayjs, { UnitType } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { data } from './data';
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat);

interface IData {
  [key: string]: {
    currency: string,
    symbol: string
  };
}

export const formatDate = (date: string, format: string = 'ddd, DD MMM YYYY'): string => {
  return dayjs(date).format(format);
};

export const formatTime = (date: string, format: string = 'HH:mm A', hour?: number, minute?: number): string => {
  if (hour && (minute || minute === 0)) {
    return dayjs(date).hour(hour)
      .minute(minute)
      .format(format);
  }
  return dayjs(date).format(format);
};

export const convertToTimezoneAndFormat = (date: string, timezone: string | undefined, format: string = 'HH:mm'): string => {
  return dayjs.utc(date).tz(timezone)
    .format(format);
};

export const getTimeZoneShortNameAndOffset = (timeZoneLongForm: string) => {
  if (!timeZoneLongForm) {
    return timeZoneLongForm;
  }
  const abbr = dayjs().tz(timeZoneLongForm, true)
    .format('z');
  const offset = dayjs().tz(timeZoneLongForm)
    .utcOffset() / 60;

  if (abbr.match('GMT')) {
    return `(${abbr})`;
  }
  return `${abbr} (GMT${offset < 0 ? offset : `+${offset}`})`;
};

export const clearCache = async (cache: any, queryName: any, selector: any, withResults: boolean = true) => {
  return cache.modify({
    fields: {
      [selector](existingItems: any) {
        let newData: any;
        if (withResults) {
          newData = cache.writeQuery({
            query: queryName,
            data: {
              [selector]: {
                results: [],
              },
            },
          });
        } else {
          newData = cache.writeQuery({
            query: queryName,
            data: {
              [selector]: null,
            },
          });
        }
        const results = existingItems?.results?.length ? existingItems.results : [];
        return [...results, newData];
      },
    },
  });
};
export const futureDate = (date: string, timeZone: string, unit: UnitType) => {
  return dayjs.tz(date, timeZone).local()
    .diff(dayjs(), unit);
};

export const subtractDateInHours = (date: string, timeZone: string, count: number) => {
  return dayjs.tz(date, timeZone).local()
    .subtract(count, 'hours');
};

export const isBeforeDate = (date: string) => {
  return dayjs().isBefore(dayjs.tz(date, 'Etc/GMT').local());
};

export const isValidEmail = (email: string) => {
  const pattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return pattern.test(email);
};

export const getQueryStringParams = (query: string) => {
  return query
    ? ((/^[?#]/).test(query) ? query.slice(1) : query)
      .split('&')
      .reduce((params: any, param) => {
        let [key, value] = param.split('=');
        params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
        return params;
      }, {})
    : {};
};

export const getCurrencyInfo = (currencyCode: string = '') => {
  const code = currencyCode.toUpperCase();

  if (!Object.prototype.hasOwnProperty.call(data, code)) {
    return {
      currency: currencyCode,
      symbol: currencyCode,
    };
  }

  return (data as IData)[code];
};

export const getDefaultLanguage = (): string => {
  const browserLanguage: string = navigator?.language.split('-')[0];

  return getLocalStorageItem('preferredLanguage') || browserLanguage;
};

export function getLocalStorageItem<T>(key: string): T | null {
  try {
    const item = localStorage.getItem(key);
    if (item) {
      return JSON.parse(item);
    } else {
      return null;
    }
  } catch (e) {
    return null;
  }
}

export const getDateDifference = (date: any) => {
  return dayjs.utc().diff(dayjs.tz(date, 'UTC'), 'minutes');
};
